import { type PointerEvent, useEffect, useRef, useState } from 'react';

interface TooltipOpenState {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    triggerHandlers: {
        onPointerDown: (event: PointerEvent<HTMLElement>) => void;
        onPointerCancel: () => void;
        onClick: () => void;
    };
}

/**
 * Open state with the hide side of `delay` applied, since Radix delays only the show, and
 * with a tap opening the bubble. Radix leaves touch out and closes on any press of the
 * target; a phone has no hover, so its readers would never see a tooltip at all. A tap
 * opens it and a tap elsewhere closes it, as the tooltip always behaved on touch.
 */
export function useTooltipOpen(hideDelay: number): TooltipOpenState {
    const [open, setOpen] = useState(false);
    const timer = useRef<number | undefined>(undefined);
    const tapping = useRef(false);
    useEffect(() => () => window.clearTimeout(timer.current), []);

    const onOpenChange = (next: boolean) => {
        // The tap's own press and click ask Radix to close; the tap means open.
        if (!next && tapping.current) return;
        window.clearTimeout(timer.current);
        if (next || hideDelay <= 0) {
            setOpen(next);
        } else {
            timer.current = window.setTimeout(() => setOpen(false), hideDelay);
        }
    };

    const triggerHandlers = {
        onPointerDown: (event: PointerEvent<HTMLElement>) => {
            if (event.pointerType !== 'touch') return;
            tapping.current = true;
            window.clearTimeout(timer.current);
            setOpen(true);
        },
        // A finger that scrolls instead of tapping sends no click.
        onPointerCancel: () => {
            tapping.current = false;
        },
        onClick: () => {
            if (!tapping.current) return;
            // Radix's own close request for this click comes after this handler.
            window.setTimeout(() => {
                tapping.current = false;
            }, 0);
        },
    };

    return { open, onOpenChange, triggerHandlers };
}
